/**
 * useHotLeadQueue Hook
 * 
 * Custom hook for managing the Hot Lead Queue - a gamified lead distribution
 * system for portfolio refinance opportunities.
 * 
 * Features:
 * - Fetches available leads sorted by deal score
 * - Supabase Realtime subscription for instant UI updates
 * - Tracks user session statistics
 * - Handles optimistic claiming with rollback
 * - Supports filtering by state, opportunity type, score range
 * 
 * @param {string} userId - Firebase UID of the current user
 * @param {object} filters - Filter criteria
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getSupabase, isSupabaseConfigured, transformHotLeadToCamelCase } from '../utils/supabase';

/**
 * Configuration constants
 */
const PAGE_SIZE = 50;
const REALTIME_CHANNEL_NAME = 'hot-lead-queue';

export function useHotLeadQueue(userId, filters = {}) {
  // State
  const [availableLeads, setAvailableLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  
  // User statistics
  const [userStats, setUserStats] = useState({
    claimedToday: 0,
    pendingUnlock: [],
    totalConverted: 0,
    currentlyActive: 0,
  });
  
  // Claiming state
  const [claimingLeadId, setClaimingLeadId] = useState(null);
  const [claimResult, setClaimResult] = useState(null);
  
  // Realtime subscription ref
  const subscriptionRef = useRef(null);
  const isInitialMount = useRef(true);

  /**
   * Fetch available leads from the queue
   */
  const fetchAvailableLeads = useCallback(async (page = 0) => {
    if (!isSupabaseConfigured()) {
      setError('Supabase is not configured');
      setLoading(false);
      return;
    }

    const supabase = getSupabase();
    setLoading(true);
    setError(null);

    try {
      // Build query for available leads
      let query = supabase
        .from('crm_hot_lead_queue')
        .select('*', { count: 'exact' })
        .eq('status', 'available')
        .order('deal_score', { ascending: false, nullsFirst: false });

      // Apply filters
      if (filters.state) {
        query = query.eq('lead_data->>borrowerState', filters.state);
      }

      if (filters.opportunityType) {
        query = query.eq('primary_opportunity', filters.opportunityType);
      }

      if (filters.minScore !== undefined && filters.minScore !== null) {
        query = query.gte('deal_score', filters.minScore);
      }

      if (filters.maxScore !== undefined && filters.maxScore !== null) {
        query = query.lte('deal_score', filters.maxScore);
      }

      if (filters.batchId) {
        query = query.eq('batch_id', filters.batchId);
      }

      if (filters.mortgageType) {
        query = query.eq('lead_data->>mortgageType', filters.mortgageType);
      }

      // Pagination
      const from = page * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;
      query = query.range(from, to);

      // Execute query
      const { data, error: queryError, count } = await query;

      if (queryError) {
        console.error('[useHotLeadQueue] Query error:', queryError);
        throw queryError;
      }

      // Transform to camelCase
      const transformedLeads = (data || []).map(transformHotLeadToCamelCase);

      if (page === 0) {
        setAvailableLeads(transformedLeads);
      } else {
        setAvailableLeads(prev => [...prev, ...transformedLeads]);
      }

      setTotalCount(count || 0);
      setHasMore((page + 1) * PAGE_SIZE < (count || 0));
      setCurrentPage(page);
      setLoading(false);

    } catch (err) {
      console.error('Error fetching hot lead queue:', err);
      setError(err.message);
      setLoading(false);
    }
  }, [filters]);

  /**
   * Fetch user's statistics and blocked leads
   */
  const fetchUserStats = useCallback(async () => {
    if (!userId || !isSupabaseConfigured()) return;

    const supabase = getSupabase();

    try {
      // Get user's converted leads from hot lead queue
      const { data: userLeads, error: leadsError } = await supabase
        .from('crm_hot_lead_queue')
        .select('id, claimed_at, converted_deal_id, deal_score, lead_data')
        .eq('claimed_by', userId)
        .in('status', ['converted', 'claimed']);

      if (leadsError) throw leadsError;

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // Calculate stats
      const claimedToday = (userLeads || []).filter(
        l => l.claimed_at && new Date(l.claimed_at) >= today
      ).length;

      // Check for leads needing activity (if we have any converted)
      let pendingUnlock = [];
      const convertedLeads = (userLeads || []).filter(l => l.converted_deal_id);
      
      if (convertedLeads.length > 0) {
        const dealIds = convertedLeads.map(l => l.converted_deal_id);
        
        // Check for recent activities
        const twoHoursAgo = new Date();
        twoHoursAgo.setHours(twoHoursAgo.getHours() - 2);
        
        const { data: activities } = await supabase
          .from('crm_activities')
          .select('deal_id')
          .in('deal_id', dealIds)
          .gte('created_at', twoHoursAgo.toISOString())
          .in('type', ['call', 'sms', 'email', 'note', 'disposition']);

        const dealsWithActivity = new Set((activities || []).map(a => a.deal_id));
        
        // Find leads without recent activity
        pendingUnlock = convertedLeads
          .filter(l => !dealsWithActivity.has(l.converted_deal_id))
          .map(l => ({
            queueId: l.id,
            dealId: l.converted_deal_id,
            claimedAt: l.claimed_at,
            borrowerName: l.lead_data?.borrowerName || 'Unknown',
            dealScore: l.deal_score,
          }));
      }

      setUserStats({
        claimedToday,
        pendingUnlock,
        totalConverted: userLeads?.length || 0,
        currentlyActive: convertedLeads.length,
        canClaim: pendingUnlock.length < 5,
      });

    } catch (err) {
      console.error('Error fetching user stats:', err);
    }
  }, [userId]);

  /**
   * Set up Supabase Realtime subscription for instant updates
   * Listens for INSERT, UPDATE, DELETE on crm_hot_lead_queue
   */
  const setupRealtimeSubscription = useCallback(() => {
    if (!isSupabaseConfigured()) return;

    const supabase = getSupabase();

    // Clean up previous subscription
    if (subscriptionRef.current) {
      subscriptionRef.current.unsubscribe();
    }

    const channel = supabase
      .channel(`${REALTIME_CHANNEL_NAME}-${Math.random().toString(36).slice(2, 8)}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'crm_hot_lead_queue',
        },
        (payload) => {
          const { eventType, new: newRecord, old: oldRecord } = payload;

          switch (eventType) {
            case 'INSERT':
              // New lead added - refresh if it's available
              if (newRecord?.status === 'available') {
                fetchAvailableLeads(0);
              }
              break;

            case 'UPDATE':
              // Lead status changed - handle claiming/recycling
              if (oldRecord?.status === 'available' && newRecord?.status !== 'available') {
                // Lead was claimed - remove from list
                setAvailableLeads(prev => 
                  prev.filter(lead => lead.id !== newRecord.id)
                );
                setTotalCount(prev => Math.max(0, prev - 1));
              } else if (oldRecord?.status !== 'available' && newRecord?.status === 'available') {
                // Lead was recycled - add back to list
                fetchAvailableLeads(0);
              }
              // Update user stats if this was their lead
              if (newRecord?.claimed_by === userId || oldRecord?.claimed_by === userId) {
                fetchUserStats();
              }
              break;

            case 'DELETE':
              // Lead deleted - remove from list
              setAvailableLeads(prev => 
                prev.filter(lead => lead.id !== oldRecord?.id)
              );
              setTotalCount(prev => Math.max(0, prev - 1));
              break;

            default:
              break;
          }
        }
      )
      .subscribe();

    subscriptionRef.current = channel;

    return () => {
      channel.unsubscribe();
    };
  }, [userId, fetchAvailableLeads, fetchUserStats]);

  /**
   * Claim a lead
   * Uses Firebase Cloud Function for transactional claiming
   */
  const claimLead = useCallback(async (leadId) => {
    if (!userId) {
      return { success: false, error: 'Not authenticated' };
    }

    if (claimingLeadId) {
      return { success: false, error: 'Already claiming a lead' };
    }

    setClaimingLeadId(leadId);
    setClaimResult(null);
    setError(null);

    // Optimistic update - remove lead from list immediately
    const leadToRemove = availableLeads.find(l => l.id === leadId);
    setAvailableLeads(prev => prev.filter(l => l.id !== leadId));

    try {
      const functions = getFunctions(undefined, 'us-east1');
      const claimHotLead = httpsCallable(functions, 'claimHotLead');
      
      const result = await claimHotLead({ leadId });
      const response = result.data;

      if (response.success) {
        // Update stats
        setUserStats(prev => ({
          ...prev,
          claimedToday: prev.claimedToday + 1,
          currentlyActive: prev.currentlyActive + 1,
        }));

        setClaimResult({
          success: true,
          dealId: response.dealId,
          contactId: response.contactId,
          lead: response.lead,
          message: response.message,
        });

        return {
          success: true,
          dealId: response.dealId,
          contactId: response.contactId,
          lead: response.lead,
        };
      } else {
        // Claim blocked - rollback optimistic update
        if (leadToRemove) {
          setAvailableLeads(prev => [leadToRemove, ...prev].sort((a, b) => 
            (b.dealScore || 0) - (a.dealScore || 0)
          ));
        }

        setClaimResult({
          success: false,
          blocked: response.blocked,
          blockedBy: response.blockedBy,
          message: response.message,
          stats: response.stats,
        });

        return {
          success: false,
          blocked: response.blocked,
          blockedBy: response.blockedBy,
          message: response.message,
        };
      }
    } catch (err) {
      console.error('Error claiming lead:', err);
      
      // Rollback optimistic update
      if (leadToRemove) {
        setAvailableLeads(prev => [leadToRemove, ...prev].sort((a, b) => 
          (b.dealScore || 0) - (a.dealScore || 0)
        ));
      }

      const errorMessage = err.message || 'Failed to claim lead';
      setError(errorMessage);
      setClaimResult({
        success: false,
        message: errorMessage,
      });

      return { success: false, error: errorMessage };
    } finally {
      setClaimingLeadId(null);
    }
  }, [userId, claimingLeadId, availableLeads]);

  /**
   * Load more leads (pagination)
   */
  const loadMore = useCallback(async () => {
    if (!hasMore || loading) return;
    await fetchAvailableLeads(currentPage + 1);
  }, [hasMore, loading, fetchAvailableLeads, currentPage]);

  /**
   * Refresh the queue
   */
  const refresh = useCallback(() => {
    fetchAvailableLeads(0);
    fetchUserStats();
  }, [fetchAvailableLeads, fetchUserStats]);

  /**
   * Clear claim result (for dismissing modals)
   */
  const clearClaimResult = useCallback(() => {
    setClaimResult(null);
  }, []);

  // Initial fetch and subscription setup
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      fetchAvailableLeads(0);
      fetchUserStats();
    }
    
    const cleanup = setupRealtimeSubscription();
    
    return () => {
      if (cleanup) cleanup();
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
      }
    };
  }, [fetchAvailableLeads, fetchUserStats, setupRealtimeSubscription]);

  // Refetch when filters change
  useEffect(() => {
    if (!isInitialMount.current) {
      fetchAvailableLeads(0);
    }
  }, [
    filters.state,
    filters.opportunityType,
    filters.minScore,
    filters.maxScore,
    filters.batchId,
    filters.mortgageType,
    fetchAvailableLeads,
  ]);

  // Computed values
  const computedStats = useMemo(() => ({
    ...userStats,
    availableCount: totalCount,
    isBlocked: userStats.pendingUnlock?.length >= 5,
    blockedCount: userStats.pendingUnlock?.length || 0,
    maxAllowed: 5,
  }), [userStats, totalCount]);

  return {
    // Lead data
    availableLeads,
    loading,
    error,
    hasMore,
    totalCount,
    
    // User stats
    userStats: computedStats,
    
    // Claiming
    claimLead,
    claimingLeadId,
    claimResult,
    clearClaimResult,
    
    // Actions
    loadMore,
    refresh,
  };
}

export default useHotLeadQueue;
